import { ChevronDown } from "lucide-react";
import { useReducedMotion } from "motion/react";
import { useId, type ReactNode } from "react";
import { Select as SelectPrimitive } from "@/components/animate-ui";
import { cn } from "@/lib/utils";
import { FieldMessage } from "./input";
import { fieldClass } from "./styles";
import { itemClass, panelClass, panelMotion } from "./menu-styles";

// Radix refuses an empty value, so the empty choice ("none") travels as this inside the control.
const NONE = "__none__";
const inner = (value?: string) => (value === "" ? NONE : value);

export type SelectOption = {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
};

export function Select({
  label,
  hideLabel = false,
  hint,
  error,
  placeholder = "Choose one",
  value,
  defaultValue,
  onValueChange,
  options,
  disabled = false,
  name,
  id,
  className,
}: {
  label: string;
  hideLabel?: boolean;
  hint?: string;
  error?: string;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  disabled?: boolean;
  name?: string;
  id?: string;
  className?: string;
}) {
  const generated = useId();
  const fieldId = id ?? generated;
  const reduce = useReducedMotion();
  const describedBy = [error ? `${fieldId}-error` : undefined, !error && hint ? `${fieldId}-hint` : undefined]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cn("grid gap-2", className)} data-slot="select">
      <label id={`${fieldId}-label`} htmlFor={fieldId} className={cn("text-sm text-foreground", hideLabel && "sr-only")}>
        {label}
      </label>
      <SelectPrimitive.Root
        value={inner(value)}
        defaultValue={inner(defaultValue)}
        onValueChange={onValueChange ? (next) => onValueChange(next === NONE ? "" : next) : undefined}
        disabled={disabled}
        name={name}
      >
        <SelectPrimitive.Trigger
          id={fieldId}
          data-field="true"
          aria-describedby={describedBy || undefined}
          aria-invalid={error ? true : undefined}
          className={cn(fieldClass, "flex items-center justify-between gap-3 text-left", error && "border-danger")}
        >
          <SelectPrimitive.Value placeholder={<span className="text-muted">{placeholder}</span>} />
          <SelectPrimitive.Icon>
            <ChevronDown aria-hidden className="size-4 text-muted" strokeWidth={1.75} />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={6}
            collisionPadding={12}
            {...panelMotion(reduce)}
            className={cn(panelClass, "w-[var(--radix-select-trigger-width)]")}
          >
            <SelectPrimitive.Viewport className="grid gap-0.5">
              {options.map((option) => (
                <SelectItem key={option.value} option={option} />
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
      <FieldMessage id={fieldId} hint={hint} error={error} />
    </div>
  );
}

function SelectItem({ option }: { option: SelectOption }) {
  return (
    <SelectPrimitive.Item value={inner(option.value)!} data-value={option.value} disabled={option.disabled} className={itemClass}>
      <span className="grid">
        <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
        {option.hint ? <span className="text-xs text-muted">{option.hint}</span> : null}
      </span>
      <SelectPrimitive.ItemIndicator>
        <span aria-hidden className="size-1.5 rounded-pill bg-current" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

export function SelectGroupLabel({ children }: { children: ReactNode }) {
  return <div className="px-3 py-1.5 text-xs text-muted">{children}</div>;
}
